import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Gear, 
  Clock,
  CalendarBlank,
  Check
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function SettingsPage() {
  const [settings, setSettings] = useState({
    operating_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    operating_hours: { start: '09:00', end: '18:00' },
    holidays: [],
    out_of_office: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newHoliday, setNewHoliday] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings`, { withCredentials: true });
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/api/settings`, settings, { withCredentials: true });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (day) => {
    const days = settings.operating_days || [];
    if (days.includes(day)) {
      setSettings({ ...settings, operating_days: days.filter(d => d !== day) });
    } else {
      setSettings({ ...settings, operating_days: [...days, day] });
    }
  };

  const addHoliday = () => {
    if (newHoliday && !settings.holidays?.includes(newHoliday)) {
      setSettings({ 
        ...settings, 
        holidays: [...(settings.holidays || []), newHoliday] 
      });
      setNewHoliday('');
    }
  };

  const removeHoliday = (holiday) => {
    setSettings({
      ...settings,
      holidays: settings.holidays?.filter(h => h !== holiday) || []
    });
  };

  if (loading) {
    return (
      <DashboardLayout title="Settings">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title="Settings" 
      subtitle="Manage your store operations"
      actions={
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-[#7d64ff] hover:bg-[#6153a7] text-white"
          data-testid="save-settings-btn"
        >
          <Check size={18} className="mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      }
    >
      <div className="space-y-6">
        {/* Out of Office Toggle */}
        <div className="bg-white border border-[#e5e5ea] p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-lg text-[#0A0A0A]">Out of Office</h3>
              <p className="text-sm text-[#5a5c87] mt-1">Temporarily pause order acceptance</p>
            </div>
            <Switch
              checked={settings.out_of_office}
              onCheckedChange={(checked) => setSettings({ ...settings, out_of_office: checked })}
              data-testid="out-of-office-toggle"
            />
          </div>
          {settings.out_of_office && (
            <div className="mt-4 p-4 bg-[#f6a609]/10 border border-[#f6a609]">
              <p className="text-sm text-[#d18f08] font-medium">
                Your store is currently set to Out of Office. New orders will not be accepted.
              </p>
            </div>
          )}
        </div>

        {/* Operating Days */}
        <div className="bg-white border border-[#e5e5ea] p-6">
          <div className="flex items-center gap-3 mb-4">
            <CalendarBlank size={24} className="text-[#7d64ff]" />
            <div>
              <h3 className="font-bold text-lg text-[#0A0A0A]">Operating Days</h3>
              <p className="text-sm text-[#5a5c87]">Select days when your store is operational</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            {DAYS_OF_WEEK.map((day) => (
              <label 
                key={day}
                className={`
                  flex items-center gap-2 p-3 border cursor-pointer transition-all duration-150
                  ${settings.operating_days?.includes(day) 
                    ? 'border-[#7d64ff] bg-[#7d64ff]/5' 
                    : 'border-[#e5e5ea] hover:border-[#5a5c87]'}
                `}
                data-testid={`day-${day.toLowerCase()}`}
              >
                <Checkbox
                  checked={settings.operating_days?.includes(day)}
                  onCheckedChange={() => toggleDay(day)}
                />
                <span className="text-sm font-medium">{day.slice(0, 3)}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Operating Hours */}
        <div className="bg-white border border-[#e5e5ea] p-6">
          <div className="flex items-center gap-3 mb-4">
            <Clock size={24} className="text-[#7d64ff]" />
            <div>
              <h3 className="font-bold text-lg text-[#0A0A0A]">Operating Hours</h3>
              <p className="text-sm text-[#5a5c87]">Set your daily business hours</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Start Time</Label>
              <Input
                type="time"
                value={settings.operating_hours?.start || '09:00'}
                onChange={(e) => setSettings({
                  ...settings,
                  operating_hours: { ...settings.operating_hours, start: e.target.value }
                })}
                className="mt-1 rounded-sm"
                data-testid="start-time"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">End Time</Label>
              <Input
                type="time"
                value={settings.operating_hours?.end || '18:00'}
                onChange={(e) => setSettings({
                  ...settings,
                  operating_hours: { ...settings.operating_hours, end: e.target.value }
                })}
                className="mt-1 rounded-sm"
                data-testid="end-time"
              />
            </div>
          </div>
        </div>

        {/* Holidays */}
        <div className="bg-white border border-[#e5e5ea] p-6">
          <div className="flex items-center gap-3 mb-4">
            <Gear size={24} className="text-[#7d64ff]" />
            <div>
              <h3 className="font-bold text-lg text-[#0A0A0A]">Holiday List</h3>
              <p className="text-sm text-[#5a5c87]">Add dates when your store will be closed</p>
            </div>
          </div>
          
          <div className="flex gap-3 mb-4">
            <Input
              type="date"
              value={newHoliday}
              onChange={(e) => setNewHoliday(e.target.value)}
              className="max-w-xs rounded-sm"
              data-testid="new-holiday-input"
            />
            <Button 
              onClick={addHoliday}
              variant="outline"
              data-testid="add-holiday-btn"
            >
              Add Holiday
            </Button>
          </div>

          {settings.holidays?.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {settings.holidays.map((holiday) => (
                <span 
                  key={holiday}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#fcfcfc] border border-[#e5e5ea] text-sm"
                >
                  {new Date(holiday).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  <button
                    onClick={() => removeHoliday(holiday)}
                    className="text-[#ea4b5a] hover:text-[#c43447]"
                    data-testid={`remove-holiday-${holiday}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#5a5c87]">No holidays added</p>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
